import { translate } from '@teamleader/i18n';
import cx from 'classnames';
import React, { ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import HeaderCell from './Cells/HeaderCell';
import StatusCell from './Cells/StatusCell';
import StatusGroupCell from './Cells/StatusGroupCell';
import TitleCell from './Cells/TitleCell';
import * as cellStyles from './Cells/styles.css';
import DataGridGroup from './DataGridGroup';
import * as styles from './styles.css';
import { useIsSmall } from '../../../../../Components/Context/Hooks/mediaQueries';
import Box from '../../../../../Components/UI/Box';
import { Project, ProjectGroup, ProjectLine, isProjectLine } from '../../../../../Types/project';
import { ProjectContext } from '../../../Context';

export interface Column {
  id: string;
  align?: 'start' | 'end';
  header: (project: Project) => ReactNode;
  line: (line: ProjectLine) => ReactNode;
  group: (line: ProjectGroup) => ReactNode;
}

const columns: Column[] = [
  {
    id: 'title',
    header: () => translate('title'),
    line: (line) => <TitleCell line={line} />,
    group: (line) => <div className={cellStyles['truncate']}>{line.title}</div>,
  },
  {
    id: 'status',
    align: 'end',
    header: (project) => (
      <HeaderCell title={translate('status')}>
        <Box marginTop={2}>
          <StatusGroupCell line={project} />
        </Box>
      </HeaderCell>
    ),
    line: (line) => <StatusCell line={line} />,
    group: (line) => <StatusGroupCell line={line} />,
  },
];

interface Props {
  isScrollable: boolean;
}

const DataGrid = ({ isScrollable }: Props) => {
  const { project } = useContext(ProjectContext);

  const [collapsedGroups, setCollapsedGroups] = useState(new Set<string>());
  const isSmall = useIsSmall();

  const handleToggle = useCallback(
    (group: ProjectGroup) => {
      if (collapsedGroups.has(group.id)) {
        collapsedGroups.delete(group.id);
      } else {
        collapsedGroups.add(group.id);
      }

      setCollapsedGroups(new Set(collapsedGroups));
    },
    [collapsedGroups],
  );

  const groups = useMemo(() => {
    const groups: ReactNode[] = [];
    let ungroupedLines: ProjectLine[] = [];

    for (const line of project!.lines) {
      if (isProjectLine(line)) {
        ungroupedLines.push(line);
        continue;
      }

      if (ungroupedLines.length) {
        groups.push(<DataGridGroup key={ungroupedLines[0].id} lines={ungroupedLines} columns={columns} />);
        ungroupedLines = [];
      }

      groups.push(
        <DataGridGroup
          key={line.id}
          group={line}
          columns={columns}
          onToggle={handleToggle}
          collapsed={collapsedGroups.has(line.id)}
        />,
      );
    }

    if (ungroupedLines.length) {
      groups.push(<DataGridGroup key={ungroupedLines[0].id} lines={ungroupedLines} columns={columns} />);
    }

    return groups;
  }, [collapsedGroups, handleToggle, project]);

  return (
    <div
      className={cx(styles['data-grid'], {
        [styles['data-grid--sticky']]: !isSmall && isScrollable,
      })}
    >
      <div
        className={styles['data-grid-header']}
        style={{ gridTemplateColumns: `6px minmax(360px, 1fr) repeat(${columns.length - 1}, 156px)` }}
      >
        {columns.map((column) => (
          <div
            key={column.id}
            className={cx(styles['data-grid-header-column'], {
              [styles['data-grid-header-column--end']]: column.align === 'end',
            })}
          >
            {column.header(project!)}
          </div>
        ))}
      </div>

      <div>{groups}</div>
    </div>
  );
};

export default DataGrid;
